import { createPortal } from "react-dom";
import styled from "styled-components";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  /* Above SidebarPanel and Navbar (1001), which modals open from. */
  z-index: 1100;
  display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  padding: ${spacing.x3}px;
  animation: ${({ $isOpen }) => ($isOpen ? "fadeIn 0.2s" : "none")};
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div<{ $isOpen: boolean; $maxWidth?: number }>`
  background-color: ${colors.background};
  border-radius: 8px;
  width: 100%;
  max-width: ${({ $maxWidth }) => $maxWidth ?? 900}px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transform: ${({ $isOpen }) => ($isOpen ? "scale(1)" : "scale(0.95)")};
  transition: transform 0.2s ease-in-out;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.x3}px;
  border-bottom: 1px solid ${colors.border};
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: ${fontSizes.large}px;
  font-weight: 600;
  color: ${colors.text};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: ${fontSizes.xlarge}px;
  color: ${colors.textMuted};
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  
  &:hover {
    background-color: ${colors.backgroundDark};
    color: ${colors.text};
  }
`;

const ModalContent = styled.div`
  flex: 1;
  padding: ${spacing.x3}px;
  overflow-y: auto;
`;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: number;
}

export const Modal = ({ isOpen, onClose, title, children, maxWidth }: ModalProps) => {
  if (!isOpen) return null;

  // Portaled so a transformed ancestor (SidebarPanel) cannot contain the
  // fixed overlay, and so the modal's buttons never submit an outer form.
  return createPortal(
    <ModalOverlay $isOpen={isOpen} onClick={onClose}>
      <ModalContainer $isOpen={isOpen} $maxWidth={maxWidth} onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{title}</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>
        <ModalContent>{children}</ModalContent>
      </ModalContainer>
    </ModalOverlay>,
    document.body,
  );
};
